/**
 * @file shape-selection-handler.ts
 * @description Manages rectangular and elliptical selection logic, including handle resizing and move-content interactions.
 */

import { g, Tool } from '@hcie/core';
import { 
    isPointInSelection, 
    buildRectSelection, 
    buildEllipseSelection, 
    buildCircleSelection, 
    buildSingleRowSelection, 
    buildSingleColumnSelection,
    moveSelectionContent 
} from '@hcie/tools';
import { SelectionMode } from '@hcie/shared';

export class ShapeSelectionHandler {
    /**
     * @function handleMouseDown
     * @description Initiates a new selection or starts a drag/resize operation on an existing one.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if the event was handled by this class.
     */
    public static handleMouseDown(event: MouseEvent): boolean {
        // [CRITICAL_LOGIC_START]
        const currentToolId: string = g.current_tool.id;
        const isShapeSelectionTool: boolean = [
            Tool.RectSelect.id, 
            Tool.EllipseSelect.id, 
            Tool.CircleSelect.id,
            (Tool as any).SingleRowSelect.id, 
            (Tool as any).SingleColumnSelect.id
        ].includes(currentToolId);

        if (!isShapeSelectionTool) {
            return false;
        }

        // STEP 1: UI Cleanup - Backup current selection mask to a buffer canvas to prevent rendering artifacts.
        if (g.selectionCanvas) {
            const backupCanvas: HTMLCanvasElement = document.createElement('canvas');
            backupCanvas.width = g.image_width;
            backupCanvas.height = g.image_height;
            const backupCtx = backupCanvas.getContext('2d');
            if (backupCtx) {
                backupCtx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
            }
            (g as any)._selectionBaseCanvas = backupCanvas;
        }

        // STEP 2: Resize Handle Detection (Ghost Mode)
        const editBounds = (g as any)._selectionEditBounds;
        if (g.isSelectionActive && (g as any).settings?.selection?.ghostMode && editBounds) {
            const handleIndex: number = this.findHoveredHandleIndex(editBounds, currentToolId === Tool.CircleSelect.id);
            
            if (handleIndex !== -1) {
                this.initiateHandleDrag(handleIndex, editBounds, currentToolId === Tool.CircleSelect.id);
                // Ensure status bar is visible during transform
                const drawingInfo = document.getElementById('drawingInfo');
                if (drawingInfo) drawingInfo.style.display = 'flex';
                return true;
            } else if (typeof (window as any).commitDrawing === 'function') {
                // If clicked away, commit the ghost selection to the final mask.
                (window as any).commitDrawing();
            }
        }

        if (g.isSelectionActive && typeof isPointInSelection === 'function' && isPointInSelection(g.pX, g.pY)) {
            g.movingSelection = true;
            g.startX = g.pX;
            g.startY = g.pY;
        } else {
            // Mode is determined by modifiers and stored for the duration of the mouse action.
            g.movingSelection = false;
            g.drawing = true;
            const selectionMode: SelectionMode = event.shiftKey ? 'add' : (event.altKey ? 'subtract' : 'replace');
            (g as any)._lastSelectionMode = selectionMode;
        }
        
        return true;
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function handleMouseMove
     * @description Handles real-time selection updates while the mouse is moving.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if the event was handled.
     */
    public static handleMouseMove(event: MouseEvent): boolean {
        const currentToolId: string = g.current_tool.id;
        const isDraggingHandle: boolean = (g as any)._draggingCornerIndex >= 0;
        const isShapeSelectionTool: boolean = [
            Tool.RectSelect.id, 
            Tool.EllipseSelect.id, 
            Tool.CircleSelect.id,
            (Tool as any).SingleRowSelect.id, 
            (Tool as any).SingleColumnSelect.id
        ].includes(currentToolId);
        
        if (!g.drawing && !g.movingSelection && !isDraggingHandle) {
            return false;
        }

        // Only handle mousemove if it's a selection tool OR we are moving/resizing it.
        // Otherwise, return false to let DrawingToolsHandler process the brush strokes (Bug Fix).
        if (!isShapeSelectionTool && !g.movingSelection && !isDraggingHandle) {
            return false;
        }

        const mode: SelectionMode = ((g as any)._lastSelectionMode || 'replace') as SelectionMode;

        // [CRITICAL_LOGIC_START]
        // CASE A: Moving the selection mask/content
        if (g.movingSelection) {
            const deltaX: number = g.pX - g.startX;
            const deltaY: number = g.pY - g.startY;
            
            if (typeof moveSelectionContent === 'function') {
                moveSelectionContent(deltaX, deltaY);
                this.updateEditBoundsInternal(deltaX, deltaY);
            }
            g.startX = g.pX;
            g.startY = g.pY;
            return true;
        }

        // CASE B: Resizing or Creating new selection area
        let startX: number = g.startX;
        let startY: number = g.startY;
        let endX: number = g.pX;
        let endY: number = g.pY;

        // Apply constraints if dragging specialized handles
        const handleIndex: number = (g as any)._draggingCornerIndex;
        const editBounds = (g as any)._selectionEditBounds;
        if (editBounds && handleIndex >= 0) {
            if (handleIndex === 4 || handleIndex === 5) { startX = editBounds.x1; endX = editBounds.x2; }
            if (handleIndex === 6 || handleIndex === 7) { startY = editBounds.y1; endY = editBounds.y2; }
        }

        this.dispatchSelectionBuilding(currentToolId, startX, startY, endX, endY, mode);

        // Update visual metadata for ghost overlay
        if ((g as any).settings?.selection?.ghostMode && handleIndex >= 0) {
            this.syncEditBounds(startX, startY, endX, endY);
        }
        // [CRITICAL_LOGIC_END]

        return true;
    }

    public static handleMouseUp(event: MouseEvent): boolean {
        const currentToolId: string = g.current_tool.id;
        const isShapeSelectionTool: boolean = [
            Tool.RectSelect.id, 
            Tool.EllipseSelect.id, 
            Tool.CircleSelect.id,
            (Tool as any).SingleRowSelect.id, 
            (Tool as any).SingleColumnSelect.id
        ].includes(currentToolId);

        if (!g.drawing && !g.movingSelection) {
            return false;
        }

        // Only handle mouseup if it's a selection tool OR we are moving a selection.
        // Otherwise, return false to let FinishHandler commit raster drawings (Bug 46 Fix).
        if (!isShapeSelectionTool && !g.movingSelection) {
            return false;
        }

        if (g.movingSelection) {
            g.movingSelection = false;
        } else {
            this.finalizeSelectionArea(event);
        }
        
        // Ensure status bar appears immediately if ghost mode is active
        if ((g as any).settings?.selection?.ghostMode && g.isSelectionActive) {
            const drawingInfo = document.getElementById('drawingInfo');
            if (drawingInfo) drawingInfo.style.display = 'flex';
        }
        
        return true;
    }

    /**
     * @function getHoverCursor
     * @description Determines the appropriate CSS cursor string based on mouse proximity to handles.
     * @returns {string | null}
     */
    public static getHoverCursor(): string | null {
        if (!g.isSelectionActive || !(g as any)._selectionEditBounds) {
            return null;
        }
        
        const editBounds = (g as any)._selectionEditBounds;
        const handleIndex: number = this.findHoveredHandleIndex(editBounds, g.current_tool.id === Tool.CircleSelect.id);
        
        if (handleIndex !== -1) {
            const cursorMap: readonly string[] = [
                'nwse-resize', 'nesw-resize', 'nesw-resize', 'nwse-resize', 
                'ns-resize', 'ns-resize', 'ew-resize', 'ew-resize', 'move'
            ];
            return cursorMap[handleIndex] || 'pointer';
        }

        if (typeof isPointInSelection === 'function' && isPointInSelection(g.pX, g.pY)) {
            return 'move';
        }

        return null;
    }

    // --- Private Helper Methods ---

    /**
     * @function findHoveredHandleIndex
     * @description Proximity check for selection handles.
     * @private
     */
    private static findHoveredHandleIndex(bounds: any, isCircle: boolean): number {
        const { x1, y1, x2, y2 } = bounds;
        const handles = [
            { x: x1, y: y1 }, { x: x2, y: y1 }, { x: x1, y: y2 }, { x: x2, y: y2 },
            { x: (x1 + x2) / 2, y: y1 }, { x: (x1 + x2) / 2, y: y2 },
            { x: x1, y: (y1 + y2) / 2 }, { x: x2, y: (y1 + y2) / 2 }
        ];
        
        if (isCircle) {
            handles.push({ x: (x1 + x2) / 2, y: (y1 + y2) / 2 });
        }

        const tolerance: number = 12 / (g.zoom || 1);
        for (let i = 0; i < handles.length; i++) {
            if (Math.abs(g.pX - handles[i].x) < tolerance && Math.abs(g.pY - handles[i].y) < tolerance) {
                return i;
            }
        }
        return -1;
    }

    /**
     * @function initiateHandleDrag
     * @description Sets up state for handle-based resizing.
     * @private
     */
    private static initiateHandleDrag(handleIndex: number, bounds: any, isCircleMove: boolean): void {
        if (isCircleMove && handleIndex === 8) {
            g.movingSelection = true;
            g.startX = g.pX;
            g.startY = g.pY;
        } else {
            (g as any)._draggingCornerIndex = handleIndex;
            const opposites: readonly number[] = [3, 2, 1, 0, 5, 4, 7, 6];
            const oppIdx = opposites[handleIndex];
            
            const { x1, y1, x2, y2 } = bounds;
            const handlePoints = [
                { x: x1, y: y1 }, { x: x2, y: y1 }, { x: x1, y: y2 }, { x: x2, y: y2 },
                { x: (x1 + x2) / 2, y: y1 }, { x: (x1 + x2) / 2, y: y2 },
                { x: x1, y: (y1 + y2) / 2 }, { x: x2, y: (y1 + y2) / 2 }
            ];

            if (oppIdx !== undefined) {
                g.startX = handlePoints[oppIdx].x;
                g.startY = handlePoints[oppIdx].y;
            }
            g.drawing = true;
        }
    }

    /**
     * @function updateEditBoundsInternal
     * @description Moves the stored metadata bounding box.
     * @private
     */
    private static updateEditBoundsInternal(deltaX: number, deltaY: number): void {
        const bounds = (g as any)._selectionEditBounds;
        if (bounds) {
            bounds.x1 += deltaX;
            bounds.x2 += deltaX;
            bounds.y1 += deltaY;
            bounds.y2 += deltaY;
        }
    }

    /**
     * @function dispatchSelectionBuilding
     * @description Triggers the appropriate core selection tool building function.
     * @private
     */
    private static dispatchSelectionBuilding(toolId: string, x1: number, y1: number, x2: number, y2: number, mode: SelectionMode): void {
        if (toolId === Tool.RectSelect.id) {
            buildRectSelection(x1, y1, x2, y2, mode);
        } else if (toolId === Tool.EllipseSelect.id) {
            buildEllipseSelection(x1, y1, x2, y2, mode);
        } else if (toolId === Tool.CircleSelect.id) {
            buildCircleSelection(x1, y1, x2, y2, mode);
        } else if (toolId === (Tool as any).SingleRowSelect.id) {
            buildSingleRowSelection(g.pY, mode);
        } else if (toolId === (Tool as any).SingleColumnSelect.id) {
            buildSingleColumnSelection(g.pX, mode);
        }
    }

    /**
     * @function syncEditBounds
     * @description Updates the selection metadata during live transformation.
     * @private
     */
    private static syncEditBounds(x1: number, y1: number, x2: number, y2: number): void {
        const minX: number = Math.min(x1, x2);
        const minY: number = Math.min(y1, y2);
        const maxX: number = Math.max(x1, x2);
        const maxY: number = Math.max(y1, y2);
        (g as any)._selectionEditBounds = {
            x1: Math.floor(minX),
            y1: Math.floor(minY),
            x2: Math.ceil(maxX),
            y2: Math.ceil(maxY)
        };
    }

    /**
     * @function finalizeSelectionArea
     * @description Wraps up coordinate calculation and state resets for shape selections.
     * @private
     */
    private static finalizeSelectionArea(event: MouseEvent): void {
        const toolId: string = g.current_tool.id;
        const mode: SelectionMode = ((g as any)._lastSelectionMode || (event.shiftKey ? 'add' : 'replace')) as SelectionMode;
        
        let x1: number = g.startX;
        let y1: number = g.startY;
        let x2: number = g.pX;
        let y2: number = g.pY;
        
        const handleIndex: number = (g as any)._draggingCornerIndex;
        const editBounds = (g as any)._selectionEditBounds;

        if (editBounds && handleIndex >= 0) {
            if (handleIndex === 4 || handleIndex === 5) { x1 = editBounds.x1; x2 = editBounds.x2; }
            if (handleIndex === 6 || handleIndex === 7) { y1 = editBounds.y1; y2 = editBounds.y2; }
        }

        // Constraints for specialized tools
        if (toolId === Tool.CircleSelect.id) {
            const deltaX: number = x2 - x1;
            const deltaY: number = y2 - y1;
            const side: number = Math.max(Math.abs(deltaX), Math.abs(deltaY));
            x2 = x1 + (deltaX < 0 ? -side : side);
            y2 = y1 + (deltaY < 0 ? -side : side);
        }

        if ((g as any).settings?.selection?.ghostMode) {
            this.syncEditBounds(x1, y1, x2, y2);
            (g as any)._lastSelectionMode = mode;
            (g as any)._draggingCornerIndex = -1;
        }
        
        g.drawing = false;
    }
}
